/**
 * Sempirical Customer Colors
 * ====================
 * Deterministic avatar colors based on customer_id.
 * Import from any page:
 *   import { getCustomerColor, getCustomerInitials } from '../../utils/customerColors'
 *
 * Same customer_id → same color, every time, on every page.
 */

// 24 high-contrast colors — ordered to maximize visual distance
// between adjacent indices so even close hashes look different.
const AVATAR_PALETTE = [
  '#1d4ed8', // blue-700
  '#b45309', // amber-700
  '#7c3aed', // violet-600
  '#059669', // emerald-600
  '#be185d', // pink-700
  '#0369a1', // sky-700
  '#c2410c', // orange-700
  '#4338ca', // indigo-700
  '#256f57', // green-700
  '#9333ea', // purple-600
  '#0e7490', // cyan-700
  '#a21caf', // fuchsia-700
  '#b91c1c', // red-700
  '#1e40af', // blue-800
  '#a16207', // yellow-700
  '#6d28d9', // violet-700
  '#047857', // emerald-700
  '#9f1239', // rose-700
  '#0c4a6e', // sky-900
  '#7c2d12', // orange-900
  '#3730a3', // indigo-800
  '#166534', // green-800
  '#6b21a8', // purple-800
  '#155e75', // cyan-800
]

/**
 * cyrb53 hash — fast, excellent distribution for short strings.
 * Returns a positive integer.
 */
function hashString(str) {
  let h1 = 0xdeadbeef
  let h2 = 0x41c6ce57
  for (let i = 0; i < str.length; i++) {
    const ch = str.charCodeAt(i)
    h1 = Math.imul(h1 ^ ch, 2654435761)
    h2 = Math.imul(h2 ^ ch, 1597334677)
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507)
  h1 ^= Math.imul(h2 ^ (h2 >>> 13), 3266489909)
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507)
  h2 ^= Math.imul(h1 ^ (h1 >>> 13), 3266489909)
  return (h2 >>> 0) * 0x100000000 + (h1 >>> 0)
}

/**
 * Get a consistent color for a customer ID.
 * Same ID always returns the same color.
 */
export function getCustomerColor(customerId) {
  if (!customerId) return AVATAR_PALETTE[0]
  const h = hashString(customerId)
  return AVATAR_PALETTE[h % AVATAR_PALETTE.length]
}

/**
 * Get initials from a company name.
 * "Demo Manufacturing" → "MM"
 * "ADP" → "AD"
 */
export function getCustomerInitials(name) {
  if (!name) return '??'
  const words = name.trim().split(/\s+/)
  if (words.length === 1) return words[0].substring(0, 2).toUpperCase()
  return (words[0][0] + words[1][0]).toUpperCase()
}
